'use client';

import { UserType } from '@/@types';
import { Avatar, Button, Element, Icon } from '@/components/core';
import { Trans, useLingui } from '@lingui/react/macro';
import { useUser } from '@/components/context/user';
import clsx from 'clsx';
import InternalLink from '@/components/core/components/organism/internal-link';
import { useLocalizedLinks } from '@/components/core/hooks';
import FollowButton from '@/components/core/components/organism/follow-button';
import { UIComponent } from '@/components/core/components';
import { Image } from '@/components/core/components/atoms';
import * as styles from './header.styles';

const ProfileHeader: UIComponent<{ user: UserType }> = ({ user, className }) => {
  const { user: currentUser } = useUser();
  const { i18n } = useLingui();
  const isCurrentUser = user.id === currentUser?.id;
  const links = useLocalizedLinks();

  const formater = new Intl.NumberFormat(i18n.locale, { notation: 'compact' });

  return (
    <Element as="header" className={clsx(styles.root, className)}>
      <div className={clsx(styles.toolbar, { [styles.allButtons]: isCurrentUser })}>
        <div className={styles.personalInfo}>
          <Avatar className={styles.avatar} src={user.avatar} level={user.level?.name} />
          <div>
            <Element as="h1" lines={1} className={styles.nickname}>
              {user.nickname}
            </Element>
            <Element as="h2" lines={1} className={styles.name}>
              {user.name} {user.lastname}
            </Element>
          </div>
        </div>
        <div className={styles.buttons}>
          {!isCurrentUser && <FollowButton as={Button} isFollowed={user.isFollowed} />}
          <Button
            variant="secondary"
            className="border-0"
            onClick={() => {
              navigator.share({
                url: links.profile(user).root,
              });
            }}
          >
            <Icon name="share" />
          </Button>
          {isCurrentUser && (
            <>
              <Button variant="borderless" className="border-0">
                <Icon name="analytics" />
              </Button>
              <Button variant="borderless" className={styles.walletBtn}>
                <Trans>Wallet</Trans>
              </Button>
              <Button variant="secondary" className="border-0" as={InternalLink} href={links.settings()}>
                <Icon name="cog" />
              </Button>
            </>
          )}
        </div>
      </div>
      <div className={styles.details}>
        <div className={styles.links}>
          {user.links?.map((link) => {
            return (
              <a target="_blank" href={link.url} className={styles.social} key={link.url} rel="nofollow noopener">
                <Image className={styles.socialLogo} width={29} height={29} src={`/social/${link.type}.png`} alt={link.type} />
                <span>{link.name}</span>
              </a>
            );
          })}
        </div>
        <div className={styles.network}>
          <span className={styles.follows}>
            <Trans>
              <b>{formater.format(user.followers!.total)}</b> Followers
            </Trans>
          </span>
          <span className={styles.follows}>
            <Trans>
              <b>{formater.format(user.follows!.total)}</b> Follows
            </Trans>
          </span>
        </div>
      </div>
    </Element>
  );
};

export default ProfileHeader;
